
import React from "react";
import { formatDistanceToNow } from "date-fns";
import { Play, Edit, Trash2, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import VideoModal from "./VideoModal";
import { Video } from "@/types/database";

interface VideoProps {
  id: string;
  source: string;
  duration?: number;
  url: string;
  thumbnailUrl?: string;
  createdAt: string;
  metadata?: Video['metadata'];
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
}

const VideoCard = ({
  id,
  source,
  duration,
  url,
  thumbnailUrl,
  createdAt,
  metadata,
  onDelete,
  onEdit
}: VideoProps) => {
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);

  // Ensure metadata is an object with the expected properties
  const safeMetadata = metadata && typeof metadata === 'object' ? metadata : {};

  // Use data from metadata with fallbacks
  const title = typeof safeMetadata.title === 'string' ? safeMetadata.title : "Untitled Video";
  const description = typeof safeMetadata.description === 'string' ? safeMetadata.description : "";

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "Unknown";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return "Unknown date";
    }
  };

  return (
    <>
      <Card className="overflow-hidden">
        <div className="relative aspect-video bg-muted">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-200">
              <span className="text-slate-500">No thumbnail</span>
            </div>
          )}
          <Button
            variant="default"
            size="icon"
            className="absolute top-2 right-2 bg-black/70 hover:bg-black/90"
            onClick={() => setIsPreviewOpen(true)}
          >
            <Play className="h-4 w-4" />
          </Button>
          {duration && (
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
              {formatDuration(duration)}
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-medium line-clamp-1">{title}</h3>
            <Badge variant="outline">{source}</Badge>
          </div>
          {description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
              {description}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Added {formatTimeAgo(createdAt)}
          </p>
        </CardContent>

        <CardFooter className="p-4 pt-0 flex gap-2 justify-between">
          <Button variant="outline" size="sm" onClick={() => onEdit(id)}>
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              asChild
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
            <Button
              variant="destructive"
              size="icon"
              onClick={() => onDelete(id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardFooter>
      </Card>

      <VideoModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        videoUrl={url}
      />
    </>
  );
};

export default VideoCard;
